import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useInitialInfoStore } from '@src/emp-3.0/store/useInitialInfo';
import NavigationBar from '@components/navigation-bar';
import Footer from './Footer';
import Icon, { CloseOutlined } from '@ant-design/icons';
import {
  SubTitle,
  HomePage,
  HomeHead,
  Welcome,
  WelcomeContainer,
  MainTitle,
  MakeDefaultAndQuickStart,
  QuickStartSection,
  QuickStartHead,
  QuickStartContent,
  Close,
  QuickStartContentText,
  QuickStartContentTextTitle,
  QuickStartContentTextContent,
  QuickStartContentVideo,
  QuickStartLink,
  Line,
  MoreFeatures,
  CapabilitySection,
  ActivationSectionContainer,
  CapabilityCardContainer,
  CapabilityCardTitle,
  CapabilityCardDesc,
  CapabilityCardLinkList,
  ActivateCard,
  KnowYourDataSectionContainer,
  KnowYourDataContainer,
  KnowYourDataItem,
  MainTitleWrapper,
  MainActionsWrapper,
} from './style';
import QuickStartIcon from '@icons/home/home-quick-start-icon.svg';
import Info from '@icons/home/home-info.svg';
import EventExploration from '@icons/home/home-exploration.svg';
import LiveLens from '@icons/home/home-livelens.svg';
import EventsIcon from '@icons/home/home-events.svg';
import DimensionsIcon from '@icons/home/home-dimensions.svg';
import MetricsIcon from '@icons/home/home-metrics.svg';
import FlowsIcon from '@icons/home/home-flow.svg';

import { useMainStore } from '@src/pages/main/store';
import { useMappingPanelStore } from '@components/mapping-panel/store';
import { QUICK_MAP_MODAL_KEY } from '@components/quick-map';
import { useDimensionPanelStore } from '@components/dimensions/store';
import { setMappingModePreset } from '@store/common/actions';
import {
  EPanelOpenMethod,
  useBlockEventSinglePanelStore,
} from '@components/block-event-single/store';
import { ERenamePreset, EEventManagementTabsType } from '@src/common/constant/enum';
import PermissionButton from '@src/emp-3.0/components/PermissionButton';
import { useDimensionWorkflow, useTopPanelStore } from '@hooks';
import { useCustomDimensionStore } from 'emp-3.0/components/Dimensions/store';

export default function HomeIndex() {
  const capabilityCardDescRefs = useRef<HTMLDivElement[]>([]);

  const openModal = useMainStore((state) => state.openModal);
  const [setMappingPanelOpen] = useMappingPanelStore((state) => [state.setMappingPanelOpen]);
  const setCurPresetName = useDimensionPanelStore((state) => state.setCurPresetName);
  const [setBlockEventSingleOpen, setPanelOpenMethod] = useBlockEventSinglePanelStore((state) => [
    state.setBlockEventSingleOpen,
    state.setPanelOpenMethod,
  ]);
  const {
    openCustomDimensionPanel,
    resetCustomDimensionPanelStore,
    openTransformPanel,
    resetTransformPanelStore,
  } = useTopPanelStore();

  const { userInfo, workbenchMode } = useInitialInfoStore();
  const isDimensionNewWorkflow = useDimensionWorkflow();
  const userName = userInfo?.username?.split('@')[0];
  const [showQuickStart, setShowQuickStart] = useState(true);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setCreateFrom } = useCustomDimensionStore((state) => state);
  const updateMaxHeight = () => {
    const maxHeight = Math.max(...capabilityCardDescRefs.current.map((ref) => ref.clientHeight));
    capabilityCardDescRefs.current.forEach((ref) => {
      ref.style.minHeight = `${maxHeight}px`;
    });
  };

  useEffect(() => {
    updateMaxHeight();
    window.addEventListener('resize', updateMaxHeight);
    return () => {
      window.removeEventListener('resize', updateMaxHeight);
    };
  }, []);

  useEffect(() => {
    setMappingPanelOpen(false);
    setBlockEventSingleOpen(false);
    resetCustomDimensionPanelStore();
    resetTransformPanelStore();
  }, []);

  return (
    <HomePage>
      {/* Head */}
      <HomeHead>
        <NavigationBar />
        <WelcomeContainer>
          <Welcome>Welcome {userName}</Welcome>
          <div>
            {/* TODO add it later */}
            {/* <MakeDefaultAndQuickStart>
            <Icon style={{ fontSize: '16px' }} component={HomeDefault} />
            Make Default
          </MakeDefaultAndQuickStart> */}
            {showQuickStart ? null : (
              <MakeDefaultAndQuickStart
                onClick={() => {
                  setShowQuickStart(true);
                }}
                style={{ marginLeft: '12px' }}
              >
                Show Quick Start
              </MakeDefaultAndQuickStart>
            )}
          </div>
        </WelcomeContainer>
      </HomeHead>
      {/* Quick Start */}
      {showQuickStart ? (
        <QuickStartSection>
          <QuickStartHead>
            <SubTitle>Quick Start: What is Activation?</SubTitle>
            <div
              onClick={() => {
                setShowQuickStart(false);
              }}
            >
              <Close>
                <CloseOutlined style={{ marginRight: '8px' }} />
                Close
              </Close>
            </div>
          </QuickStartHead>
          <QuickStartContent>
            <QuickStartContentText>
              <QuickStartContentTextTitle>
                Activate your data with CONVIVA Operational Data Platform. <br />
                Build stateful metrics that monitor your time-to-value and drive business outcomes
                using our NO-CODE UI.
              </QuickStartContentTextTitle>
              <QuickStartContentTextContent>
                Activation provides revolutionary capabilities to democratize the process of metric
                building even for non-technical business team. <br />
                The semantics mapping tool allows you to harmonize the data that comes from
                different platforms and make them actionable with semantics transformation. <br />
              </QuickStartContentTextContent>
              <QuickStartLink>
                <a
                  href="https://docs.conviva.ai/sei/app/activation/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none' }}
                >
                  <Icon component={Info} /> Learn more
                </a>
              </QuickStartLink>
              <Icon
                component={QuickStartIcon}
                style={{
                  position: 'absolute',
                  right: '24px',
                  bottom: 0,
                  fontSize: '128px',
                  width: '128px',
                  height: '128px',
                }}
              />
            </QuickStartContentText>
            <QuickStartContentVideo>
              {/* <iframe
                width="450"
                height="255"
                src="https://www.youtube.com/embed/0elmGPEc8-Y"
                title="Semantic Mapper - Activate Data that Matters Most"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerpolicy="strict-origin-when-cross-origin"
                allowfullscreen="allowfullscreen"
              ></iframe> */}
              <video
                width="460"
                height="260"
                controls
                style={{ display: 'block', borderRadius: '4px' }}
              >
                <source
                  src="https://vdash-prod-v2.vision.conviva.com/activation/activation-0708.mp4"
                  type="video/mp4"
                />
              </video>
            </QuickStartContentVideo>
          </QuickStartContent>
        </QuickStartSection>
      ) : null}
      {/* Capability */}
      <CapabilitySection>
        <MainActionsWrapper>
          <ActivationSectionContainer>
            <MainTitleWrapper>
              <MainTitle>Activate...</MainTitle>
            </MainTitleWrapper>
            <CapabilityCardContainer>
              {/* New version start */}
              <ActivateCard>
                <Icon
                  style={{ fontSize: '40px', margin: '0 0 8px 0', fillOpacity: '0.25' }}
                  component={EventsIcon}
                />
                <CapabilityCardTitle>Events</CapabilityCardTitle>
                <CapabilityCardDesc
                  ref={(el) => {
                    if (el) capabilityCardDescRefs.current[0] = el;
                  }}
                >
                  Activate only the events required to power the metrics you need
                </CapabilityCardDesc>
                <PermissionButton
                  onClick={() => {
                    setMappingPanelOpen(true);
                    dispatch(setMappingModePreset());
                    setCurPresetName('Explore');
                    navigate(`/semantic-mapper`);
                  }}
                  styleType="styled"
                  style={{ padding: '4px 15px', fontWeight: 400 }}
                >
                  Map Event
                </PermissionButton>
                <Line />
                <MoreFeatures>More Features</MoreFeatures>
                <CapabilityCardLinkList>
                  <PermissionButton
                    onClick={() => {
                      navigate(`/semantic-mapper`);
                      // wait for page to mount
                      setTimeout(() => {
                        openModal(QUICK_MAP_MODAL_KEY);
                      }, 100);
                    }}
                    type="primary"
                  >
                    <li>
                      <a
                        href={`semantic-mapper`}
                        style={{ textDecoration: 'none' }}
                        onClick={(e) => {
                          e.preventDefault();
                        }}
                      >
                        Map Events In Bulk (Quick Map)
                      </a>
                    </li>
                  </PermissionButton>
                  {!workbenchMode && (
                    <PermissionButton
                      onClick={() => {
                        setBlockEventSingleOpen(true);
                        setPanelOpenMethod(EPanelOpenMethod.CREATE);
                        navigate(`/semantic-mapper`);
                      }}
                      type="primary"
                    >
                      <li>
                        <a
                          href={`semantic-mapper`}
                          style={{ textDecoration: 'none' }}
                          onClick={(e) => {
                            e.preventDefault();
                          }}
                        >
                          Block Events At The Sensor
                        </a>
                      </li>
                    </PermissionButton>
                  )}
                  <li>
                    <a
                      href={`management`}
                      style={{ textDecoration: 'none' }}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(
                          `/management/mapped-events?activeTab=${EEventManagementTabsType.MAPPED_EVENT}`,
                        );
                      }}
                    >
                      Manage Events And NW Requests
                    </a>
                  </li>
                </CapabilityCardLinkList>
              </ActivateCard>
              <ActivateCard>
                <Icon
                  style={{ fontSize: '40px', margin: '0 0 8px 0', color: 'rgba(0,0,0,0.25)' }}
                  component={FlowsIcon}
                />
                <CapabilityCardTitle>Flows</CapabilityCardTitle>
                <CapabilityCardDesc
                  ref={(el) => {
                    if (el) capabilityCardDescRefs.current[1] = el;
                  }}
                >
                  Measure and optimize critical user journeys with in your app
                </CapabilityCardDesc>
                <PermissionButton
                  onClick={() => {
                    navigate(`/workflow/flow/new`);
                  }}
                  styleType="styled"
                  style={{ padding: '4px 15px', fontWeight: 400 }}
                >
                  Create Flow
                </PermissionButton>
                <Line />
                <MoreFeatures>More Features</MoreFeatures>
                <CapabilityCardLinkList>
                  <li>
                    <a
                      href={`management/flows`}
                      style={{ textDecoration: 'none' }}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/management/flows`);
                      }}
                    >
                      Manage Flows
                    </a>
                  </li>
                </CapabilityCardLinkList>
              </ActivateCard>
              <ActivateCard>
                <Icon style={{ fontSize: '40px', margin: '0 0 8px 0' }} component={MetricsIcon} />
                <CapabilityCardTitle>Metrics</CapabilityCardTitle>
                <CapabilityCardDesc
                  ref={(el) => {
                    if (el) capabilityCardDescRefs.current[1] = el;
                  }}
                >
                  Build experience (inc stateful) metrics using our NO-CODE UI
                </CapabilityCardDesc>
                <PermissionButton
                  onClick={() => {
                    navigate(`/workflow/metric/new`);
                  }}
                  styleType="styled"
                  style={{ padding: '4px 15px', fontWeight: 400 }}
                >
                  Create Individual Metric
                </PermissionButton>
                <Line />
                <MoreFeatures>More Features</MoreFeatures>
                <CapabilityCardLinkList>
                  <li>
                    <a
                      href={`management/metrics`}
                      style={{ textDecoration: 'none' }}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/management/metrics`);
                      }}
                    >
                      Manage Individual Metrics
                    </a>
                  </li>
                </CapabilityCardLinkList>
              </ActivateCard>

              <ActivateCard>
                <Icon
                  style={{ fontSize: '40px', margin: '0 0 8px 0', fillOpacity: 0.25 }}
                  component={DimensionsIcon}
                />
                <CapabilityCardTitle>Dimensions</CapabilityCardTitle>
                <CapabilityCardDesc
                  ref={(el) => {
                    if (el) capabilityCardDescRefs.current[2] = el;
                  }}
                >
                  Define dimensions based on global metadata and event metadata
                </CapabilityCardDesc>
                <PermissionButton
                  onClick={() => {
                    if (isDimensionNewWorkflow) {
                      setCreateFrom('otherEntry');
                      navigate(`/workflow/dimension/new`);
                    } else {
                      resetCustomDimensionPanelStore();
                      openCustomDimensionPanel();
                      navigate(`/semantic-mapper`);
                    }
                  }}
                  styleType="styled"
                  style={{ padding: '4px 15px', fontWeight: 400 }}
                >
                  Create New Dimension
                </PermissionButton>
                <Line />
                <MoreFeatures>More Features</MoreFeatures>
                <CapabilityCardLinkList>
                  <PermissionButton
                    onClick={() => {
                      setCurPresetName(ERenamePreset.PAGE);
                      if (isDimensionNewWorkflow) {
                        navigate(`/workflow/rename/new`);
                      } else {
                        resetTransformPanelStore();
                        openTransformPanel();
                        navigate(`/semantic-mapper`);
                      }
                    }}
                    type="primary"
                  >
                    <li>
                      <a
                        href={`/workflow/rename/new`}
                        style={{ textDecoration: 'none' }}
                        onClick={(e) => {
                          e.preventDefault();
                        }}
                      >
                        Rename Page/Screen Titles
                      </a>
                    </li>
                  </PermissionButton>
                  <li>
                    <a
                      href={`management`}
                      style={{ textDecoration: 'none' }}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(
                          `/management/custom-dimension?activeTab=${EEventManagementTabsType.CUSTOM_DIMENSION}`,
                        );
                      }}
                    >
                      Manage Custom Dimensions
                    </a>
                  </li>
                </CapabilityCardLinkList>
              </ActivateCard>
            </CapabilityCardContainer>
          </ActivationSectionContainer>

          <KnowYourDataSectionContainer>
            <MainTitleWrapper>
              <MainTitle>Know your data...</MainTitle>
            </MainTitleWrapper>
            <KnowYourDataContainer>
              <KnowYourDataItem
                onClick={() => {
                  navigate(`/semantic-mapper`);
                }}
              >
                <Icon
                  style={{ fontSize: '40px', margin: '0 0 8px 0', fillOpacity: '0.25' }}
                  component={EventExploration}
                />
                <CapabilityCardTitle>Event Exploration</CapabilityCardTitle>
                <CapabilityCardDesc>Understand events and event metadata.</CapabilityCardDesc>
              </KnowYourDataItem>
              {!workbenchMode && (
                <KnowYourDataItem
                  onClick={() => {
                    navigate(`/livelens`);
                  }}
                >
                  <Icon
                    style={{ fontSize: '40px', margin: '0 0 8px 0', fillOpacity: '0.25' }}
                    component={LiveLens}
                  />
                  <CapabilityCardTitle>LiveLens</CapabilityCardTitle>
                  <CapabilityCardDesc>
                    Use your app for realtime understanding of what events are triggered by specific
                    user actions.
                  </CapabilityCardDesc>
                </KnowYourDataItem>
              )}
            </KnowYourDataContainer>
          </KnowYourDataSectionContainer>
        </MainActionsWrapper>
      </CapabilitySection>
      {/* Footer */}
      <Footer></Footer>
    </HomePage>
  );
}
